import { useCallback, useEffect, useRef, useState } from 'react';
import { cajaService, configuracionService, facturasService } from '../../services/api';
import { abrirTicket } from '../../utils/ticket';
import { descargarTicketPdf } from '../../utils/ticketPdf';
import { descargarBlob } from '../../utils/descargar';
import { useMetodosPago } from '../../hooks/useMetodosPago';
import { useToast } from '../../components/ui/Toast';
import ModalLlaveAcceso from '../../components/ModalLlaveAcceso';
import { btnPrimario, btnSecundario, campo, chip, pesos } from '../../components/ui/estilos';
import { Encabezado } from '../reportes/ui';
import { usePeriodo } from '../reportes/periodo';
import { DialogoFactura, DialogoMetodo } from './DialogosTicket';

const n = (v: any) => parseFloat(v || 0) || 0;

export const folio = (t: any) => `#${String(t.id).slice(0, 6).toUpperCase()}`;
const cliente = (t: any) => t.mesa_numero != null ? `Mesa ${t.mesa_numero}` : `Para llevar${t.cliente_nombre ? ` · ${t.cliente_nombre}` : ''}`;
const hora = (s: string) => new Date(s).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
const accion: React.CSSProperties = { ...btnSecundario, height: 48, fontSize: 15 };
const FACTURADO = { bg: 'var(--ok-bg)', fg: 'var(--ok)' };

export default function Tickets() {
  const { metodos, label } = useMetodosPago();
  const [toast, say] = useToast();
  // Mismo encabezado y periodo que los reportes (Hoy / Semana / Mes / Personalizado).
  const p = usePeriodo('hoy');
  const [desde, hasta] = p.actual;
  const [busca, setBusca] = useState('');
  const [tickets, setTickets] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [sel, setSel] = useState<any>(null);
  const [detalle, setDetalle] = useState<any>(null);
  const [factura, setFactura] = useState<any>(null);
  const [config, setConfig] = useState<Record<string, string>>({});
  const [dialogo, setDialogo] = useState<null | 'metodo' | 'factura' | 'reabrir' | 'cancelar'>(null);
  const detalleCache = useRef<Record<string, any>>({});

  const buscar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await cajaService.getHistorial({ fecha_inicio: desde, fecha_fin: hasta, limit: 500 });
      setTickets(r.data.tickets || []);
    } catch { say('No se pudieron cargar los tickets.'); }
    finally { setCargando(false); }
  }, [desde, hasta, say]);

  useEffect(() => { buscar(); }, [buscar]);
  useEffect(() => { configuracionService.get().then(r => setConfig(r.data)).catch(() => {}); }, []);

  const obtenerDetalle = async (t: any) => {
    if (!detalleCache.current[t.id]) detalleCache.current[t.id] = (await cajaService.getTicketDetalle(t.id)).data;
    return detalleCache.current[t.id];
  };

  const elegir = async (t: any) => {
    setSel(t); setDetalle(null); setFactura(null);
    try {
      setDetalle(await obtenerDetalle(t));
      if (t.facturado) setFactura((await facturasService.getPorPago(t.id)).data);
    } catch { say('No se pudo cargar el detalle del ticket.'); }
  };

  const refrescar = async () => {
    if (sel) delete detalleCache.current[sel.id];
    await buscar();
    setSel(null); setDetalle(null);
  };

  // 1º la impresora térmica de caja (sin diálogo); si no hay, el navegador.
  const reimprimir = async () => {
    try {
      const r = await cajaService.reimprimirTicket(sel.id);
      if (r.data?.ok) { say('Ticket enviado a la impresora de caja.'); return; }
    } catch { /* cae al navegador */ }
    try {
      const res = await abrirTicket((await obtenerDetalle(sel)).ticket);
      if (res === 'error') say('No se pudo imprimir el ticket.');
    } catch { say('No se pudo obtener el ticket.'); }
  };

  const pdf = async () => {
    try { descargarTicketPdf((await obtenerDetalle(sel)).ticket); }
    catch { say('No se pudo generar el PDF.'); }
  };

  const descargarFactura = async (tipo: 'pdf' | 'xml') => {
    try {
      const r = tipo === 'pdf' ? await facturasService.descargarPdf(factura.id) : await facturasService.descargarXml(factura.id);
      descargarBlob(r.data, `factura-${factura.folio || factura.id}.${tipo}`);
    } catch { say(`No se pudo descargar el ${tipo.toUpperCase()}.`); }
  };

  // Personas que estuvieron (reporte de clientes): se guarda en cada toque.
  const cambiarPersonas = async (valor: number) => {
    if (valor < 0) return;
    try {
      await cajaService.cambiarComensales(sel.id, valor);
      setSel({ ...sel, comensales: valor });
      setTickets(ts => ts.map(x => x.id === sel.id ? { ...x, comensales: valor } : x));
    } catch (e: any) { say(e.response?.data?.error || 'No se pudieron cambiar las personas.'); }
  };

  const q = busca.trim().toLowerCase();
  const filas = tickets.filter(t => !q || [folio(t), cliente(t), t.mesero || ''].some(s => s.toLowerCase().includes(q)));
  const totalFiltro = filas.reduce((s, t) => s + n(t.monto_total), 0);
  const metodoDe = (t: any) => [t.metodo, t.metodo_2].filter(Boolean).map(label).join(' + ') || '—';
  const tk = detalle?.ticket;

  return (
    <div className="paneles" style={{ height: 'calc(var(--vh100, 100vh) - 124px)', minHeight: 480, margin: '-22px -24px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) clamp(300px, 32%, 400px)' }}>
      <section aria-label="Tickets" style={{ minHeight: 0, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Encabezado titulo="Historial de tickets" p={p} volverA={{ to: '/turno', texto: 'Turno' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por folio, mesa o mesero…" aria-label="Buscar ticket"
            style={{ ...campo, flex: 1, minWidth: 220, width: 'auto', height: 48 }} />
        </div>

        <div className="tk-table" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden' }}>
          <div className="tk-row" role="row" style={{ padding: '12px 16px', background: 'var(--well)', fontSize: 13, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.02em' }}>
            <span>FOLIO</span><span>MESA / CLIENTE</span><span className="tk-opcional">MESERO</span><span>HORA</span><span className="tk-opcional">MÉTODO</span><span style={{ textAlign: 'right' }}>TOTAL</span>
          </div>
          {cargando && <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)' }}>Cargando tickets…</div>}
          {!cargando && !filas.length && <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)', fontSize: 15 }}>No hay tickets con ese filtro.</div>}
          {!cargando && filas.map(t => (
            <button key={t.id} onClick={() => elegir(t)} className="tk-row" aria-pressed={sel?.id === t.id}
              style={{ width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', borderTop: '1px solid var(--line-2)', cursor: 'pointer', fontSize: 15, color: 'var(--ink)', background: sel?.id === t.id ? 'var(--well)' : 'var(--surface)' }}>
              <span style={{ fontWeight: 700 }}>{folio(t)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span className="tk-ellipsis">{cliente(t)}</span>
                {t.facturado && <span style={{ ...chip(FACTURADO.bg, FACTURADO.fg), fontSize: 12, padding: '2px 7px' }}>CFDI</span>}
              </span>
              <span className="tk-opcional tk-ellipsis" style={{ color: 'var(--muted)' }}>{t.mesero}</span>
              <span style={{ color: 'var(--muted)' }}>{hora(t.created_at)}</span>
              <span className="tk-opcional tk-ellipsis" style={{ color: 'var(--muted)' }}>{metodoDe(t)}</span>
              <span style={{ textAlign: 'right', fontWeight: 700 }}>{pesos(n(t.monto_total))}</span>
            </button>
          ))}
        </div>
        {filas.length > 0 && <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 700 }}>{filas.length} tickets · {pesos(totalFiltro)}</div>}
      </section>

      <aside aria-label="Detalle del ticket" style={{ borderLeft: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {!sel && <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 16 }}>Elige un ticket para ver su detalle.</div>}
        {sel && !tk && <div style={{ padding: 20, color: 'var(--muted)' }}>Cargando detalle…</div>}
        {sel && tk && (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{folio(sel)}</h2>
                {sel.facturado && <span style={chip(FACTURADO.bg, FACTURADO.fg)}>Facturado</span>}
              </div>
              <div style={{ fontSize: 15, color: 'var(--muted)' }}>{cliente(sel)} · {sel.mesero} · {new Date(sel.created_at).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
            </div>

            <div role="group" aria-label="Personas que estuvieron" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>Personas</span>
              <button onClick={() => cambiarPersonas(n(sel.comensales) - 1)} disabled={n(sel.comensales) <= 0} aria-label="Menos personas"
                style={{ ...btnSecundario, width: 44, height: 44, padding: 0, fontSize: 20 }}>−</button>
              <span aria-live="polite" style={{ minWidth: 32, textAlign: 'center', fontSize: 20, fontWeight: 700 }}>{n(sel.comensales)}</span>
              <button onClick={() => cambiarPersonas(n(sel.comensales) + 1)} aria-label="Más personas"
                style={{ ...btnSecundario, width: 44, height: 44, padding: 0, fontSize: 20 }}>+</button>
            </div>

            <div style={{ background: 'var(--bg)', borderRadius: 12, padding: '14px 16px' }}>
              {tk.items.map((i: any, k: number) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--line-2)', fontSize: 15 }}>
                  <span style={{ fontWeight: 700, width: 28 }}>{i.cantidad}×</span><span style={{ flex: 1 }}>{i.producto}</span><span style={{ fontWeight: 700 }}>{pesos(n(i.importe))}</span>
                </div>
              ))}
              {[['Subtotal', pesos(tk.subtotal)], ['IVA', pesos(tk.iva)], ...(tk.descuento_monto > 0 ? [['Descuento', `−${pesos(tk.descuento_monto)}`]] : []), ...(tk.propina > 0 ? [['Propina', pesos(tk.propina)]] : [])].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, fontSize: 14, color: k === 'Descuento' ? 'var(--danger)' : 'var(--muted)' }}><span>{k}</span><span>{v}</span></div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, marginTop: 4, borderTop: '1px solid var(--line)', fontSize: 18, fontWeight: 700 }}><span>Total</span><span>{pesos(tk.total)}</span></div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)' }}>Pago</div>
              {(tk.metodo_2
                ? [[tk.metodo, tk.monto_metodo_1], [tk.metodo_2, tk.monto_metodo_2]]
                : [[tk.metodo, tk.total + (tk.propina || 0)]]).map(([m, v]: any) => (
                <div key={m} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}><span>{label(m)}</span><span style={{ fontWeight: 700 }}>{pesos(n(v))}</span></div>
              ))}
              {tk.cambio > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: 'var(--muted)' }}><span>Cambio</span><span>{pesos(tk.cambio)}</span></div>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6 }}>
              <button onClick={reimprimir} style={accion}>Reimprimir ticket</button>
              <button onClick={pdf} style={accion}>Descargar PDF</button>
              <button onClick={() => setDialogo('metodo')} disabled={sel.facturado} title={sel.facturado ? 'Facturado: cancela la factura para cambiar la forma de pago' : undefined}
                style={{ ...accion, opacity: sel.facturado ? .45 : 1 }}>Corregir pago</button>
              <button onClick={() => setDialogo('reabrir')} style={accion}>Reabrir cuenta</button>
              {factura?.estado === 'timbrada' ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => descargarFactura('pdf')} style={{ ...accion, flex: 1 }}>Factura PDF</button>
                  <button onClick={() => descargarFactura('xml')} style={{ ...accion, flex: 1 }}>Factura XML</button>
                </div>
              ) : (
                <button onClick={() => setDialogo('factura')} style={{ ...btnPrimario, height: 52, fontSize: 16, borderRadius: 10 }}>Facturar (CFDI)</button>
              )}
              <button onClick={() => setDialogo('cancelar')} style={{ ...accion, color: 'var(--danger)', borderColor: 'var(--danger-line)' }}>Cancelar ticket</button>
            </div>
          </div>
        )}
      </aside>

      {dialogo === 'metodo' && sel && (
        <DialogoMetodo t={sel} metodos={metodos.filter((m: any) => m.activo)} etiquetaMetodo={label} onCerrar={() => setDialogo(null)}
          onListo={() => { setDialogo(null); say(`Pago de ${folio(sel)} corregido.`); refrescar(); }} />
      )}
      {dialogo === 'factura' && sel && (
        <DialogoFactura t={sel} config={config} onCerrar={() => setDialogo(null)}
          onListo={f => { setDialogo(null); say(f.email_error ? `CFDI generado; no se pudo enviar por correo: ${f.email_error}` : `CFDI de ${folio(sel)} generado.`); setFactura(f); setTickets(ts => ts.map(x => x.id === sel.id ? { ...x, facturado: f.estado === 'timbrada' } : x)); setSel({ ...sel, facturado: f.estado === 'timbrada' }); }} />
      )}
      {dialogo === 'reabrir' && sel && (
        <ModalLlaveAcceso permiso="puede_cobrar" titulo={`Reabrir ${folio(sel)}`} descripcion="La cuenta vuelve a la mesa sin cobrar; hay que volver a cobrarla."
          onCancel={() => setDialogo(null)}
          onSuccess={() => { setDialogo(null); cajaService.reabrirCuenta(sel.id).then(() => { say(`${folio(sel)} reabierto. La cuenta vuelve a Cuentas.`); refrescar(); }).catch((e: any) => say(e.response?.data?.error || 'No se pudo reabrir.')); }} />
      )}
      {dialogo === 'cancelar' && sel && (
        <ModalLlaveAcceso permiso="puede_cancelar_cuenta" titulo={`Cancelar ${folio(sel)}`} descripcion={`Anula el ticket por ${pesos(n(sel.monto_total))}. Deja de contar como venta y el insumo regresa al inventario.`}
          onCancel={() => setDialogo(null)}
          onSuccess={() => { setDialogo(null); cajaService.cancelarTicket(sel.id).then(() => { say(`${folio(sel)} cancelado.`); refrescar(); }).catch((e: any) => say(e.response?.data?.error || 'No se pudo cancelar el ticket.')); }} />
      )}
      {toast}
    </div>
  );
}
